import { Component, inject, input, type OnDestroy } from "@angular/core";
import { AlertService, Button, ButtonIcon, Icon } from "@ui";
import { distinctUntilChanged, type Subscription, skip } from "rxjs";
import type { ColumnMeta, Corpus } from "@pienso/ingest-types/corpus.interface";
import { ES_TYPE } from "../../services/elastic-search.service";
import { TRANSFORMER_ACTION, TRANSFORMER_STATE } from "../abstract-transformer.service";
import type { TransformerToolbar } from "../toolbar.interface";
import { TransformerService } from "../transformer.service";
import { ColumnTypeService } from "./column-type.service";
import { ColumnTypeInstructionsTooltipComponent } from "./column-type-instructions-tooltip.component";

@Component({
  selector: "app-columntype-toolbar",
  styleUrls: ["./column-type-toolbar.component.scss"],
  template: `
    <div class="p-flex p-flex-col p-flex-between p-h-full p-min-h-full">
      <div class="p-flex p-flex-col">
        <div class="p-flex-row-xs p-flex-center-y p-p-xxs">
          <div class="p-flex-row-xxs p-flex-center-y">
            <p-button-icon-ghosted-lg icon="arrow-left" (click)="cancel()" />
            <span class="p-text-body">Edit column type</span>
          </div>
          <app-column-type-instructions-tooltip />
        </div>
        <div class="p-divider"></div>
      </div>

      <div class="p-flex p-flex-col">
        <div class="p-divider"></div>
        <div class="p-flex-col-xs p-px-md p-py-sm column-type-footer">
          <p-button-primary-md [loading]="isSaving" [disabled]="!canSave()" (click)="save()">
            @if (!isSaving) {
              <p-icon-lg icon="checkmark"/>
            }
            Save changes</p-button-primary-md>
          <p-button-secondary-md (click)="cancel()">
            <p-icon-lg icon="cross"/>Cancel</p-button-secondary-md>
        </div>
      </div>
    </div>
    `,
  imports: [Button, ButtonIcon, ColumnTypeInstructionsTooltipComponent, Icon],
})
export class ColumnTypeToolbarComponent implements TransformerToolbar, OnDestroy {
  private service = inject(ColumnTypeService);
  private alertService = inject(AlertService);
  private transformerService = inject(TransformerService);

  protected readonly ES_TYPE = ES_TYPE;

  readonly corpus = input.required<Corpus>();

  private isChanged = false;
  private subscription: Subscription | null = null;

  // columnOrder excludes synthetic columns like __rowNumber__ and matches the grid's ordering.
  get columnIds(): string[] {
    return (this.corpus().columnMeta as ColumnMeta).columnOrder ?? [];
  }

  constructor() {
    const service = this.service;

    this.subscription = service.setTypes$.subscribe((colIds) => {
      this.isChanged = colIds.length > 0;
    });

    // skip(1) drops the replayed current state, which is already DONE when editing an existing transform.
    this.subscription.add(
      service.state$.pipe(skip(1), distinctUntilChanged()).subscribe((state) => {
        if (state === TRANSFORMER_STATE.ERROR) {
          this.alertService.error("Problem saving column types.");
        } else if (state === TRANSFORMER_STATE.DONE) {
          this.alertService.success("Changes saved.");
        }
      }),
    );
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  canSave() {
    return this.isChanged && this.service.state === TRANSFORMER_STATE.EDITING && !this.hasIncompleteDateFormat();
  }

  get isSaving(): boolean {
    return this.service.state === TRANSFORMER_STATE.PENDING;
  }

  typeFor(columnId: string): ES_TYPE | undefined {
    return this.service.columnTypes?.[columnId]?.type;
  }

  formatFor(columnId: string): string | undefined {
    return this.service.columnTypes?.[columnId]?.format;
  }

  private hasIncompleteDateFormat(): boolean {
    return this.columnIds.some((columnId) => this.typeFor(columnId) === ES_TYPE.DATE && !this.formatFor(columnId));
  }

  // TRANSFORMER_ACTION.CANCEL only rejects the pending transform promise; this is what closes the toolbar.
  cancel(): void {
    this.transformerService.cancel();
  }

  save() {
    this.service.doAction(TRANSFORMER_ACTION.SAVE);
  }
}
